import { enableAllPlugins, produce } from 'immer';

enableAllPlugins();

export function initialSelectionsState() {
  return {
    personnel: new Map(),
    roleIds: new Set(),
    teamIds: new Set(),
  };
}

export default function selectionsReducer(state, action) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case 'PERSONNEL_SELECTED':
        handlePersonnelSelection({ draftState, ...action.payload });
        break;
      case 'PERSONNEL_DESELECTED':
        handlePersonnelDeselection({ draftState, ...action.payload });
        break;
      case 'RESOURCE_SELECTED':
        handleResourceSelection({ draftState, ...action.payload });
        break;
      case 'RESOURCE_DESELECTED':
        handleResourceDeselection({ draftState, ...action.payload });
        break;
      case 'RESOURCE_ADDABLE_COUNTS_FETCHED':
        handleImplicitResourceSelection({
          addableCounts: action.payload.addableCounts,
          draftState,
        });
        break;
      case 'PROJECT_PERSONNEL_TAKEN':
        handleProjectPersonnelTaken({ draftState, ...action.payload });
        break;
      case 'SELECTIONS_RESET':
        return initialSelectionsState();
      default:
        return state;
    }
  });
}

function handlePersonnelSelection({ draftState, personnel, resource }) {
  // If personnel is being selected from a resource, but that resource is no longer selected, ignore the payload.
  if (resource) {
    const { resourceId, resourceType } = resource;

    if (!draftState[resourceType].has(resourceId)) return;
  }

  personnel.forEach(({ personnelId, roleIds, teamIds }) => {
    draftState.personnel.set(personnelId, {
      roleIds: new Set(roleIds),
      teamIds: new Set(teamIds),
    });
  });
}

function handlePersonnelDeselection({ draftState, personnelId }) {
  const { roleIds, teamIds } = draftState.personnel.get(personnelId);

  draftState.personnel.delete(personnelId);

  (function deselectTheirResourcesAsPersonnelAreNoLongerAllSelected() {
    roleIds.forEach((roleId) => draftState.roleIds.delete(roleId));
    teamIds.forEach((teamId) => draftState.teamIds.delete(teamId));
  })();
}

function handleResourceSelection({ draftState, resourceId, resourceType }) {
  draftState[resourceType].add(resourceId);
}

function handleResourceDeselection({ draftState, resourceId, resourceType }) {
  draftState[resourceType].delete(resourceId);

  [...draftState.personnel].forEach(([personnelId, personnelResources]) => {
    if (!personnelResources[resourceType].has(resourceId)) return;
    if (isPersonnelStillSelected({ draftState, personnelResources })) return;

    draftState.personnel.delete(personnelId);
  });
}

function isPersonnelStillSelected({
  draftState,
  personnelResources: { roleIds, teamIds },
}) {
  return (
    [...roleIds].some((roleId) => draftState.roleIds.has(roleId)) ||
    [...teamIds].some((teamId) => draftState.teamIds.has(teamId))
  );
}

function handleImplicitResourceSelection({ addableCounts, draftState }) {
  Object.entries(addableCounts).forEach(([resourceType, resourceCounts]) => {
    resourceCounts.forEach((addableCount, resourceId) => {
      if (!addableCount) return;

      const selectedCount = Array.from(draftState.personnel.values()).filter(
        (personnelSelection) =>
          personnelSelection[resourceType].has(resourceId),
      ).length;

      if (selectedCount === addableCount) {
        draftState[resourceType].add(resourceId);
      }
    });
  });
}

function handleProjectPersonnelTaken({ draftState, personnelIds }) {
  personnelIds.forEach((personnelId) => {
    draftState.personnel.delete(personnelId);
  });
}
